import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Table, DatePicker, message, Button, Dropdown, Select, Modal, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { PlusOutlined, MoreOutlined } from "@ant-design/icons";
import { svg } from "../../../Assets/svg";
import { hr } from "../../../Assets/hr";
import { getDecryptedUserData, decryptByText } from "../../../helpers/bycrypt";
import { RootState, useAppDispatch, useAppSelector } from "../../../store";
import { GetTeamMemberListAPI, GetEmployeeDetailsAPI } from "../../Redux/teamMember";
import { getTimeOffRequestsAPI, deleteTimeOffRequestAPI } from "../TeamMembers/timeOffApis";
import { BookTimeOffModal } from "../TeamMembers/Components/Modals/BookTimeOffModal";
import FullScreenLoader from "../../Components/FullScreenLoader";
import NoDataMessage from "../../Components/NoData";
import AutoCloseTooltip from "../../Components/AutoCloseTooltip";
import CommonDeleteModal from "../../Components/Modals/CommonDeleteModal";
import CommonFilePreviewModal from "../../Components/CommonFilePreviewModal";
import { getFullAwsUrl, ROLE, buildAllowanceYearOptions, getBusinessCurrentYear } from "../../../helpers/utils";
import {
  PagePermissionNameData,
  usePermissionsChecker,
} from "../../../helpers/permissionModule";
import NoPermissionPage from "../../Components/NoPermissionPage";
import NotificationButton from "../../Components/NotificationButton";
import {
  formatDateWithBusinessTZ, formatCalendarDateDisplay, formatTimeOffSlot, formatTimeOffDuration, getTimeOffTimeRange, getAllowanceDeducted,
  getEmploymentEndDateString, isTimeOffPastEmploymentEndDate
} from "../../../helpers/moment";
import TimeOffTypeBadge from "../../Components/TimeOffTypeBadge";

const { RangePicker } = DatePicker;

const timeTypeLabels = {
  1: "Morning",
  2: "Afternoon",
  3: "Lunchtime",
  4: "End of Day",
};

const ReasonCell = ({ text, isMobile = false, onViewMore }: { text: string; isMobile?: boolean; onViewMore: () => void }) => {
  const maxLength = isMobile ? 35 : 70;

  if (!text || text.length <= maxLength) {
    return (
      <div style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
        {text || "-"}
      </div>
    );
  }

  const visible = text.substring(0, maxLength) + "...";

  return (
    <div style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
      {visible}
      <span
        className="text-primary ms-1 cursor-pointer fw-bold"
        style={{ whiteSpace: "nowrap" }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onViewMore();
        }}
      >
        View More
      </span>
    </div>
  );
};

const CLOCK_IN_TIME_OFF_YEAR_KEY = "clock_in_time_off_history_year";

const ClockInTimeOffHistory: React.FC<{ id: string }> = ({ id }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentBusinessYear = getBusinessCurrentYear();

  const getInitialYear = (): string => {
    const stateYear =
      (location.state as any)?.selectedYear ?? (location.state as any)?.year;
    if (stateYear !== undefined && stateYear !== null) {
      const s = String(stateYear);
      return s.toLowerCase() === "all" ? "" : s;
    }
    const saved = sessionStorage.getItem(CLOCK_IN_TIME_OFF_YEAR_KEY);
    if (saved !== null) {
      return saved.toLowerCase() === "all" ? "" : saved;
    }
    const paramYear = searchParams.get("year");
    if (paramYear !== null) {
      return paramYear.toLowerCase() === "all" ? "" : paramYear;
    }
    return currentBusinessYear.toString();
  };

  const { teamMemberList, employeeDetails } = useAppSelector(
    (state: RootState) => state.teamMember,
  );
  const USER_PERMISSIONS = usePermissionsChecker(
    PagePermissionNameData?.CLOCK_IN_HISTORY,
  );
  const { authLoading } = useAppSelector((state: RootState) => state?.loading);

  const userData = getDecryptedUserData();
  const userRole = userData?.role?.key || userData?.role;
  const isEmployeeOrPermitted = Boolean(
    userData?.isEmployee ||
    userRole === "employee" ||
    String(userData?.role?.name || "").toLowerCase() === "employee" ||
    USER_PERMISSIONS?.READ ||
    userRole === ROLE.ADMIN
  );

  const canRead = isEmployeeOrPermitted;
  const canAdd = Boolean(USER_PERMISSIONS?.ADD || isEmployeeOrPermitted);
  const canModify = Boolean(USER_PERMISSIONS?.MODIFY || isEmployeeOrPermitted);
  const canRemove = Boolean(USER_PERMISSIONS?.REMOVE || isEmployeeOrPermitted);

  const [selectedYear, setSelectedYear] = useState<string>(getInitialYear);
  const [selectedEmployee, setSelectedEmployee] = useState<string>(!id || id === "all" ? "" : id);
  const targetEmployeeId = selectedEmployee || (!id || id === "all" ? userData?._id : id);
  const isFetchingEmpDetailsRef = useRef(false);
  const fetchedEmpDetailsRef = useRef<string | null>(null);

  useEffect(() => {
    if (
      targetEmployeeId &&
      fetchedEmpDetailsRef.current !== targetEmployeeId &&
      !isFetchingEmpDetailsRef.current
    ) {
      fetchedEmpDetailsRef.current = targetEmployeeId;
      isFetchingEmpDetailsRef.current = true;
      dispatch(
        GetEmployeeDetailsAPI(targetEmployeeId, () => {
          isFetchingEmpDetailsRef.current = false;
        }) as any,
      );
    }
  }, [targetEmployeeId, dispatch]);

  const [timeOffRequests, setTimeOffRequests] = useState<any[]>([]);

  const targetEmployee = useMemo(() => {
    return teamMemberList?.find((emp: any) => emp._id === targetEmployeeId) || null;
  }, [teamMemberList, targetEmployeeId]);

  const rawEndDate =
    (employeeDetails?.user === targetEmployeeId || employeeDetails?._id === targetEmployeeId)
      ? employeeDetails?.basic_details?.employment_end_date
      : targetEmployee?.employee_details?.basic_details?.employment_end_date ||
      targetEmployee?.basic_details?.employment_end_date ||
      (targetEmployeeId === userData?._id
        ? (userData?.employment_end_date ||
          userData?.employee_details?.basic_details?.employment_end_date ||
          userData?.basic_details?.employment_end_date)
        : null);

  const empEndDateStr = getEmploymentEndDateString(rawEndDate);
  const todayStr = dayjs().format("YYYY-MM-DD");
  const isEmploymentEnded = Boolean(empEndDateStr && todayStr > empEndDateStr);
  const [loading, setLoading] = useState<boolean>(false);
  const [openBookTimeOffModal, setOpenBookTimeOffModal] = useState(false);
  const [editData, setEditData] = useState<any>(null);

  const [employeePage, setEmployeePage] = useState(1);
  const [hasMoreEmployees, setHasMoreEmployees] = useState(true);

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewFiles, setPreviewFiles] = useState<any[]>([]);
  const [viewMoreRecord, setViewMoreRecord] = useState<any>(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 991.98);

  useEffect(() => {
    if (id && id !== "all") {
      setSelectedEmployee(id);
    }
  }, [id]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 991.98);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setEmployeePage(1);
    dispatch(GetTeamMemberListAPI({ limit: 10, page: 1, isEmployee: true }, (hasMore: boolean) => setHasMoreEmployees(hasMore)));
  }, [dispatch]);

  const handleYearFilterChange = (val: string) => {
    setSelectedYear(val);
    sessionStorage.setItem(CLOCK_IN_TIME_OFF_YEAR_KEY, val || "all");
  };

  useEffect(() => {
    sessionStorage.setItem(CLOCK_IN_TIME_OFF_YEAR_KEY, selectedYear || "all");
  }, [selectedYear]);

  useEffect(() => {
    const paramYear = searchParams.get("year");
    if (paramYear !== null) {
      const normalized = paramYear.toLowerCase() === "all" ? "" : paramYear;
      if (normalized !== selectedYear) {
        setSelectedYear(normalized);
      }
    }
  }, [searchParams, selectedYear]);

  const [availableYears, setAvailableYears] = useState<(number | string)[]>([]);

  const yearOptions = useMemo(() => {
    return buildAllowanceYearOptions(
      availableYears.length > 0
        ? availableYears
        : selectedYear
          ? [selectedYear]
          : undefined,
    );
  }, [availableYears, selectedYear]);

  useEffect(() => {
    if (selectedYear && yearOptions.length > 0) {
      const isValid = yearOptions.some((opt) => opt.value === Number(selectedYear));
      if (!isValid) {
        const fallbackYear = currentBusinessYear.toString();
        setSelectedYear(fallbackYear);
        sessionStorage.setItem(CLOCK_IN_TIME_OFF_YEAR_KEY, fallbackYear);
      }
    }
  }, [selectedYear, yearOptions, currentBusinessYear]);

  const fetchTimeOffRequests = () => {
    setLoading(true);
    const payload: any = {
      user: selectedEmployee || undefined,
      status: "all",
      limit: 1000,
      is_manage_allowance: true,
    };
    if (selectedYear) {
      payload.year = selectedYear;
    }
    dispatch(
      getTimeOffRequestsAPI({
        payload,
        onSuccess: (resData: any) => {
          if (Array.isArray(resData?.years)) {
            setAvailableYears(resData.years);
          }
          const items = Array.isArray(resData) ? resData : (resData?.data || []);
          setTimeOffRequests(items);
          setLoading(false);
        },
        onError: () => {
          setTimeOffRequests([]);
          setLoading(false);
        },
      }),
    );
  };

  useEffect(() => {
    fetchTimeOffRequests();
  }, [selectedEmployee, selectedYear, dispatch]);

  const handlePopupScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const { target } = e as any;
    if (
      target.scrollTop + target.offsetHeight >= target.scrollHeight - 10 &&
      hasMoreEmployees
    ) {
      const nextPage = employeePage + 1;
      setEmployeePage(nextPage);
      dispatch(
        GetTeamMemberListAPI({ limit: 10, page: nextPage, isEmployee: true }, (hasMore: boolean) => {
          setHasMoreEmployees(hasMore);
        })
      );
    }
  };

  const employeeOptions = teamMemberList?.map((emp: any) => ({
    label: emp.name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim(),
    value: emp._id,
  })) || [];

  const columns: ColumnsType<any> = [
    {
      title: "Employee Name",
      dataIndex: "name",
      key: "name",
      hidden: !!id && id !== "all",
      width: 150,
      render: (name: any, record: any) => {
        const firstName = record?.user?.first_name ? decryptByText(record.user.first_name) : "";
        const lastName = record?.user?.last_name ? decryptByText(record.user.last_name) : "";
        const displayName = `${firstName} ${lastName}`.trim();
        return (
          <div className="d-flex align-items-center gap-2">
            <div
              style={{
                wordWrap: "break-word",
                wordBreak: "break-all",
                whiteSpace: "normal",
              }}
            >
              {displayName ? displayName : "-"}
            </div>
          </div>
        );
      },
    },
    {
      title: "Date",
      dataIndex: "starting",
      key: "date",
      render: (_, record: any) => {
        if (!record.starting?.date) return "-";
        const date = formatCalendarDateDisplay(record.starting.date, "DD-MM-YYYY");
        const rangeTooltip = getTimeOffTimeRange(record);
        return (
          <AutoCloseTooltip title={rangeTooltip} trigger={["click", "hover"]}>
            <span style={{ whiteSpace: "nowrap" }} className="cursor-pointer">
              {date}
            </span>
          </AutoCloseTooltip>
        );
      },
    },
    {
      title: "Duration",
      key: "duration",
      render: (record: any) => {
        const durationText = formatTimeOffDuration(record);
        const rangeTooltip = getTimeOffTimeRange(record);
        return (
          <AutoCloseTooltip title={rangeTooltip}>
            <span style={{ whiteSpace: "nowrap" }} className="cursor-pointer">
              {durationText}
            </span>
          </AutoCloseTooltip>
        );
      },
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      // width: 240,
      render: (type: any) => (
        <div className="d-flex align-items-center flex-wrap gap-1">
          <span style={{ wordWrap: "break-word", wordBreak: "break-word", whiteSpace: "normal" }}>
            {type?.name || "-"}
          </span>
          {type?.type && <TimeOffTypeBadge type={type.type} />}
        </div>
      ),
    },
    {
      title: "Deducted",
      key: "allowanceDeducted",
      render: (_: any, record: any) => (
        <span style={{ whiteSpace: "nowrap" }}>
          {getAllowanceDeducted(record)}
        </span>
      ),
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      width: 320,
      render: (reason: any, record: any) => <ReasonCell text={reason?.note} onViewMore={() => setViewMoreRecord(record)} />,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        let color = "default";
        if (status === "approved") color = "success";
        else if (status === "rejected") color = "danger";
        else if (status === "pending") color = "warning";
        return <span className={`text-${color} text-capitalize`}>{status || "Pending"}</span>;
      },
    },
    {
      title: "Attachments",
      dataIndex: "reason",
      key: "attachments",
      align: "center",
      render: (reason) => {
        if (!reason?.attachments || reason.attachments.length === 0) return "-";
        return (
          <span
            className="text-primary cursor-pointer text-decoration-underline"
            onClick={() => {
              setPreviewFiles(
                reason.attachments.map((att: any) => {
                  const rawName =
                    att.attachment.split("/").pop() || "Attachment";
                  // Remove UUID if present (UUID length is 36 chars + underscore)
                  const cleanName = /^[a-f0-9-]{36}_/.test(rawName)
                    ? rawName.substring(37)
                    : rawName;

                  return {
                    url: getFullAwsUrl(att.attachment),
                    name: cleanName,
                  };
                }),
              );
              setPreviewModalVisible(true);
            }}
          >
            {reason.attachments.length} file(s)
          </span>
        );
      },
    },
    {
      title: "Created By",
      dataIndex: "created_by",
      key: "created_by",
      hidden: !!id && id !== "all",
      width: 120,
      render: (created_by: any, record: any) => {
        const firstName = created_by?.first_name ? decryptByText(created_by.first_name) : "";
        const lastName = created_by?.last_name ? decryptByText(created_by.last_name) : "";
        const displayName = `${firstName} ${lastName}`.trim();
        return displayName ? displayName : "-";
      },
    },
    {
      title: "Created On",
      dataIndex: "created_at",
      key: "created_at",
      hidden: !!id && id !== "all",
      width: 130,
      render: (created_at: any) => (
        <div
          style={{
            wordWrap: "break-word",
            wordBreak: "break-all",
            whiteSpace: "normal",
          }}
        >
          {created_at ? formatDateWithBusinessTZ(created_at, "DD-MM-YYYY, hh:mm A") : "-"}
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      hidden: !canModify && !canRemove,
      render: (record) => {
        if (record.status && record.status !== "pending") return "-";

        const actionItems = [];
        if (canModify && !isEmploymentEnded && !isTimeOffPastEmploymentEndDate(record)) {
          actionItems.push({
            key: "1",
            label: "Modify",
            onClick: () => {
              setEditData(record);
              setOpenBookTimeOffModal(true);
            },
          });
        }
        if (canRemove) {
          actionItems.push({
            key: "2",
            label: <span className="text-danger">Remove</span>,
            onClick: () => {
              setDeleteId(record._id);
              setDeleteModalVisible(true);
            },
          });
        }

        if (actionItems.length === 0) return "-";

        return (
          <Dropdown
            menu={{ items: actionItems }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <div
              className="bg-dark text-white rounded d-flex align-items-center justify-content-center cursor-pointer svg-28 mx-auto"
              style={{ width: 24, height: 24 }}
            >
              <span className="svg-28 svg-outline-primary">
                {svg?.tableMoreOption}
              </span>
            </div>
          </Dropdown>
        );
      },
    },
  ];

  if (authLoading || loading) return <FullScreenLoader />;
  if (!canRead) return <NoPermissionPage />;

  return (
    <>
      <div className="h-100 mobile-font-s">
        <div className="d-flex flex-md-nowrap flex-wrap gap-md-3 gap-2 justify-content-between align-items-center mb-3">
          <h2 className="text-black fs-36 fw-bolder page-title mb-0 d-flex align-items-center gap-3 order-md-1 order-1 w-mob-65">
            {/* <span
              className="cursor-pointer svg-24"
              onClick={() => navigate(-1)}
            >
              {svg?.backArrow || "←"}
            </span> */}
            <span>Time Off History</span>
          </h2>
          <div className="d-flex flex-sm-nowrap flex-wrap align-items-center gap-md-3 gap-2 order-lg-2 order-md-2 order-3 ms-md-auto w-mob-100">
            {(!id || id === "all") && (
              <div className="whatsBannedDropdown filterCommonDropdown mw-100 d-inline-block d-flex align-items-center w-mob-100">
                <Select
                  showSearch
                  allowClear
                  placeholder="Select Employee"
                  className="form-select form-select-lg"
                  style={{ minWidth: 220 }}
                  value={selectedEmployee || undefined}
                  onChange={(val) => setSelectedEmployee(val || "")}
                  options={employeeOptions}
                  filterOption={(input, option) =>
                    String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  onPopupScroll={handlePopupScroll}
                />
              </div>
            )}
            <div className="whatsBannedDropdown filterCommonDropdown mw-100 d-inline-block">
              <Select
                value={selectedYear ? Number(selectedYear) : undefined}
                className="form-select form-select-lg"
                onChange={(val) => handleYearFilterChange(String(val))}
                suffixIcon={svg?.selectDownArrow}
              >
                {yearOptions.map((opt) => (
                  <Select.Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>
          {canAdd && !isEmploymentEnded && (
            <div className="d-flex align-items-center gap-2 order-lg-3 order-md-3 order-2">
              <AutoCloseTooltip title="Book Time Off" placement="bottomRight">
                <div
                  className="bg-dark text-white rounded d-flex align-items-center justify-content-center cursor-pointer header-add-btn"
                  onClick={() => setOpenBookTimeOffModal(true)}
                >
                  <span className="svg-28 svg-outline-primary d-flex align-items-center justify-content-center">
                    <PlusOutlined className="fs-6 fw-bold" />
                  </span>
                </div>
              </AutoCloseTooltip>
            </div>
          )}
        </div>

        <div className={`border-0 rounded-4 flex-fill overflow-y-auto ${isMobile ? '' : 'card page-table-card shadow'}`}>
          <div className={`overflow-y-auto ${isMobile ? '' : 'card-body'}`}>
            {!isMobile ? (
              <div className="w-100 flex-grow-1" style={{ overflowY: "auto" }}>
                <Table
                  className="table-stripped-1 h-100 w-100 table-font-14"
                  columns={columns}
                  dataSource={timeOffRequests}
                  pagination={false}
                  bordered
                  locale={{
                    emptyText: <NoDataMessage icon={svg?.events?.noData} text="You do not have any time off history yet." />,
                  }}
                  rowKey="_id"
                />
              </div>
            ) : (
              <div className="px-0 pb-3 w-100" style={{ overflowY: "auto", flexGrow: 1 }}>
                <div className="d-flex flex-column gap-3 w-100">
                  {timeOffRequests?.length === 0 ? (
                    <NoDataMessage icon={svg?.events?.noData} text="You do not have any time off requests yet." />
                  ) : (
                    timeOffRequests?.map((record) => {
                      const firstName = record?.user?.first_name ? decryptByText(record.user.first_name) : "";
                      const lastName = record?.user?.last_name ? decryptByText(record.user.last_name) : "";
                      const displayName = `${firstName} ${lastName}`.trim();

                      let color = "default";
                      if (record.status === "approved") color = "success";
                      else if (record.status === "rejected") color = "danger";
                      else if (record.status === "pending") color = "warning";

                      const startingDate = record.starting?.date ? formatCalendarDateDisplay(record.starting.date, "DD-MM-YYYY") : "-";
                      const startingTime =
                        typeof record.starting?.time_type === "string" && record.starting?.time_type.includes(":")
                          ? formatTimeOffSlot(record.starting.time_type)
                          : timeTypeLabels[record.starting?.time_type as keyof typeof timeTypeLabels] || "";

                      const endingDate = record.ending?.date ? formatCalendarDateDisplay(record.ending.date, "DD-MM-YYYY") : "-";
                      const endingTime =
                        typeof record.ending?.time_type === "string" && record.ending?.time_type.includes(":")
                          ? formatTimeOffSlot(record.ending.time_type)
                          : timeTypeLabels[record.ending?.time_type as keyof typeof timeTypeLabels] || "";

                      return (
                        <div className="w-100" key={record._id}>
                          <div className="card mobile-table-card mobile-font-s rounded-4 p-3 shadow-sm">
                            {/* Card Header */}
                            <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                              <div className="d-flex align-items-center flex-wrap gap-1">
                                <span className="fw-bold fs-6" style={{ color: "#333" }}>
                                  {record.type?.name || "-"}
                                </span>
                                {record.type?.type && <TimeOffTypeBadge type={record.type.type} />}
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <span className={`text-${color} text-capitalize fw-bold`}>
                                  {record.status || "Pending"}
                                </span>
                                {(canModify || canRemove) && record.status === "pending" && (
                                  <Dropdown
                                    menu={{
                                      items: [
                                        ...(canModify && !isEmploymentEnded && !isTimeOffPastEmploymentEndDate(record)
                                          ? [
                                            {
                                              key: "1",
                                              label: "Modify",
                                              onClick: () => {
                                                setEditData(record);
                                                setOpenBookTimeOffModal(true);
                                              },
                                            },
                                          ]
                                          : []),
                                        ...(canRemove
                                          ? [
                                            {
                                              key: "2",
                                              label: <span className="text-danger">Remove</span>,
                                              onClick: () => {
                                                setDeleteId(record._id);
                                                setDeleteModalVisible(true);
                                              },
                                            },
                                          ]
                                          : []),
                                      ],
                                    }}
                                    trigger={["click"]}
                                    placement="bottomRight"
                                  >
                                    <span className="cursor-pointer d-flex align-items-center justify-content-center svg-24" style={{ padding: "4px" }}>
                                      {svg?.tableMoreOption}
                                    </span>
                                  </Dropdown>
                                )}
                              </div>
                            </div>

                            {/* Card Details Grid */}
                            <div className="mobile-card-details fs-6 fs-6-mob">
                              <div className="row gy-2">
                                {(!id || id === "all") && (
                                  <div className="col-12 d-flex justify-content-between align-items-center">
                                    <span className="text-secondary fw-medium">Employee</span>
                                    <span className="fw-medium text-dark">{displayName || "-"}</span>
                                  </div>
                                )}

                                <div className="col-12 d-flex justify-content-between align-items-center">
                                  <span className="text-secondary fw-medium">Date</span>
                                  <span className="fw-medium text-dark text-end">
                                    {record.starting?.date ? (
                                      <AutoCloseTooltip title={getTimeOffTimeRange(record)} trigger={["click", "hover"]}>
                                        <span className="cursor-pointer">{startingDate}</span>
                                      </AutoCloseTooltip>
                                    ) : (
                                      startingDate
                                    )}
                                  </span>
                                </div>

                                <div className="col-12 d-flex justify-content-between align-items-center">
                                  <span className="text-secondary fw-medium">Duration</span>
                                  <span className="fw-medium text-dark text-end">
                                    {formatTimeOffDuration(record)}
                                  </span>
                                </div>

                                <div className="col-12 d-flex justify-content-between align-items-center">
                                  <span className="text-secondary fw-medium">Allowance Deducted</span>
                                  <span className="fw-medium text-dark text-end">
                                    {getAllowanceDeducted(record)}
                                  </span>
                                </div>

                                <div className="col-12 d-flex justify-content-between align-items-start">
                                  <span className="text-secondary fw-medium">Reason</span>
                                  <div className="fw-medium text-dark text-end" style={{ maxWidth: "60%" }}>
                                    <ReasonCell text={record.reason?.note} isMobile={true} onViewMore={() => setViewMoreRecord(record)} />
                                  </div>
                                </div>

                                <div className="col-12 d-flex justify-content-between align-items-center">
                                  <span className="text-secondary fw-medium">Attachments</span>
                                  <span className="fw-medium text-dark">
                                    {!record.reason?.attachments || record.reason.attachments.length === 0 ? (
                                      "-"
                                    ) : (
                                      <span
                                        className="text-primary cursor-pointer text-decoration-underline"
                                        onClick={() => {
                                          setPreviewFiles(
                                            record.reason.attachments.map((att: any) => {
                                              const rawName = att.attachment.split("/").pop() || "Attachment";
                                              const cleanName = /^[a-f0-9-]{36}_/.test(rawName)
                                                ? rawName.substring(37)
                                                : rawName;
                                              return {
                                                url: getFullAwsUrl(att.attachment),
                                                name: cleanName,
                                              };
                                            })
                                          );
                                          setPreviewModalVisible(true);
                                        }}
                                      >
                                        {record.reason.attachments.length} file(s)
                                      </span>
                                    )}
                                  </span>
                                </div>

                                {(!id || id === "all") && (
                                  <>
                                    <div className="col-12 d-flex justify-content-between align-items-center">
                                      <span className="text-secondary fw-medium">Created By</span>
                                      <span className="fw-medium text-dark text-end">
                                        {record.created_by
                                          ? `${record.created_by.first_name ? decryptByText(record.created_by.first_name) : ""} ${record.created_by.last_name ? decryptByText(record.created_by.last_name) : ""
                                            }`.trim() || "-"
                                          : "-"}
                                      </span>
                                    </div>
                                    <div className="col-12 d-flex justify-content-between align-items-center">
                                      <span className="text-secondary fw-medium">Created On</span>
                                      <span className="fw-medium text-dark text-end">
                                        {record.created_at ? formatDateWithBusinessTZ(record.created_at, "DD-MM-YYYY, hh:mm A") : "-"}
                                      </span>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {openBookTimeOffModal && (
        <BookTimeOffModal
          visible={openBookTimeOffModal}
          onCancel={() => {
            setOpenBookTimeOffModal(false);
            setEditData(null);
          }}
          onSuccess={() => {
            fetchTimeOffRequests();
            if (targetEmployeeId) {
              dispatch(GetEmployeeDetailsAPI(targetEmployeeId) as any);
            }
          }}
          employeeId={selectedEmployee}
          employeeName={
            teamMemberList?.find((emp: any) => emp._id === selectedEmployee)
              ?.name ||
            teamMemberList?.find((emp: any) => emp._id === selectedEmployee)
              ?.first_name
          }
          editData={editData}
          existingRequests={timeOffRequests}
        />
      )}

      {deleteModalVisible && (
        <CommonDeleteModal
          visible={deleteModalVisible}
          setIsModalVisible={setDeleteModalVisible}
          setIsDeleteId={setDeleteId}
          handleDelete={() => {
            if (deleteId) {
              setLoading(true);
              dispatch(
                deleteTimeOffRequestAPI({
                  id: deleteId,
                  onSuccess: () => {
                    setDeleteModalVisible(false);
                    setDeleteId(null);
                    fetchTimeOffRequests();
                    if (targetEmployeeId) {
                      dispatch(GetEmployeeDetailsAPI(targetEmployeeId) as any);
                    }
                  },
                  onError: () => {
                    setLoading(false);
                  },
                }),
              );
            }
          }}
          isLoading={loading}
          title="Delete Time Off Request"
          description="Are you sure you want to delete this time off request?"
        />
      )}

      {viewMoreRecord && (
        <Modal
          title={
            <div className="text-center fs-4 fw-bolder">
              Time Off Request Details
            </div>
          }
          open={!!viewMoreRecord}
          onCancel={() => setViewMoreRecord(null)}
          width={"40rem"}
          closeIcon={svg?.closeIcon}
          centered
          footer={null}
          className="custom-modal"
          destroyOnClose
        >
          <div className="py-3 text-dark">
            <div className="d-flex flex-column gap-3">
              <div className="d-flex justify-content-between align-items-center border-bottom pb-2">
                <span className="text-secondary fw-medium">Employee</span>
                <span className="fw-bold">
                  {viewMoreRecord.user ? `${viewMoreRecord.user.first_name ? decryptByText(viewMoreRecord.user.first_name) : ""} ${viewMoreRecord.user.last_name ? decryptByText(viewMoreRecord.user.last_name) : ""}`.trim() : "-"}
                </span>
              </div>
              <div className="d-flex justify-content-between align-items-center border-bottom pb-2">
                <span className="text-secondary fw-medium">Type</span>
                <span className="fw-medium">{viewMoreRecord.type?.name || "-"}</span>
              </div>
              <div className="d-flex justify-content-between align-items-center border-bottom pb-2">
                <span className="text-secondary fw-medium">Status</span>
                <span className={`text-${viewMoreRecord.status === "approved" ? "success" : viewMoreRecord.status === "rejected" ? "danger" : "warning"} text-capitalize fw-bold`}>
                  {viewMoreRecord.status || "Pending"}
                </span>
              </div>
              <div className="d-flex justify-content-between align-items-center border-bottom pb-2">
                <span className="text-secondary fw-medium">Date</span>
                <span className="fw-medium text-end">
                  {viewMoreRecord.starting?.date ? (
                    <AutoCloseTooltip title={getTimeOffTimeRange(viewMoreRecord)} trigger={["click", "hover"]}>
                      <span className="cursor-pointer">
                        {formatCalendarDateDisplay(viewMoreRecord.starting.date, "DD-MM-YYYY")}
                      </span>
                    </AutoCloseTooltip>
                  ) : "-"}
                </span>
              </div>
              <div className="d-flex justify-content-between align-items-center border-bottom pb-2">
                <span className="text-secondary fw-medium">Duration</span>
                <span className="fw-medium text-end">
                  {formatTimeOffDuration(viewMoreRecord)}
                </span>
              </div>
              <div className="d-flex flex-column gap-1 border-bottom pb-2">
                <span className="text-secondary fw-medium">Reason</span>
                <span className="fw-medium" style={{ whiteSpace: "normal", wordWrap: "break-word" }}>{viewMoreRecord.reason?.note || "-"}</span>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-secondary fw-medium">Attachments</span>
                <span className="fw-medium">
                  {(!viewMoreRecord.reason?.attachments || viewMoreRecord.reason.attachments.length === 0) ? "-" : (
                    <span
                      className="text-primary cursor-pointer text-decoration-underline"
                      onClick={() => {
                        setPreviewFiles(
                          viewMoreRecord.reason.attachments.map((att: any) => {
                            const rawName = att.attachment.split("/").pop() || "Attachment";
                            const cleanName = /^[a-f0-9-]{36}_/.test(rawName) ? rawName.substring(37) : rawName;
                            return {
                              url: getFullAwsUrl(att.attachment),
                              name: cleanName,
                            };
                          })
                        );
                        setPreviewModalVisible(true);
                      }}
                    >
                      {viewMoreRecord.reason.attachments.length} file(s)
                    </span>
                  )}
                </span>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {previewModalVisible && (
        <CommonFilePreviewModal
          visible={previewModalVisible}
          onClose={() => setPreviewModalVisible(false)}
          files={previewFiles}
        />
      )}
    </>
  );
};

export default ClockInTimeOffHistory;
